import _ from 'lodash'

/**
 * Shared visibility rules for a report settings schema.
 *
 * These mirror ReportSettingsPanel's own `isVisible`/`visibleSections` exactly,
 * so a host component (e.g. GroupReportTemplate) can ask "does this schema
 * render anything in this context?" without duplicating the panel's logic or
 * mounting it. `context` is the same shape the panel builds for `conditionScope`
 * (settings, interval, isSavable, and a `can(permission)` callback).
 */

const passesShowIf = (entry, context) =>
    ! _.isFunction(entry.showIf) || entry.showIf(context)

const passesSavableOnly = (entry, context) =>
    entry.savableOnly !== true || context.isSavable === true

const passesPermission = (entry, context) => {
    if (_.isNil(entry.permission)) {
        return true
    }
    return _.isFunction(context.can) ? context.can(entry.permission) : true
}

const isVisible = (entry, context) =>
    passesShowIf(entry, context)
    && passesSavableOnly(entry, context)
    && passesPermission(entry, context)

/**
 * True when the schema would render at least one field in the given context.
 * A section counts only if it is itself visible AND has a visible field, so an
 * all-`showIf`-hidden schema (e.g. a report whose only section is
 * `showIf: isSavable` on a manual run) correctly reports "no settings".
 */
export const schemaHasVisibleFields = (schema, context) =>
    (schema?.sections ?? []).some(section =>
        isVisible(section, context)
        && (section.fields ?? []).some(field => isVisible(field, context))
    )
